import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type BiddingApprovalMode,
  BiddingApprovalState,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'

import type { SubmitSendTiming } from '~/components/bidding/common/SubmitConfirmModal'
import { JEPX_AREA_CODES } from '~/models/area'
import { isDirectSubmitMode } from '~/pages/SpotBidding/models/approvalModeResolve'

/**
 * 承認申請・直接提出 (AC-BSP-007 / #7758) で 1 つの下書きの申請セットに送る指定。
 * `TransitionBiddingSpotDrafts` の target と同形。
 *
 * - 全札を指す (承認モードなし): 分割せず、 保存済みの承認モードで遷移する
 *   (設定との食い違いは backend が AC-BSP-122 で拒否する)
 * - 一部の札だけを指す (承認モードあり): 指定した札を元の申請セットのまま遷移し、 残りの札を
 *   新しい下書きへ移す。 承認モードは申請・提出する側と残りの側を別々に解決して送る
 */
export type DraftSubmitTarget = {
  id: string
  expectedUpdatedAt: Timestamp | undefined
  spotBidIds: string[]
  approvalMode?: BiddingApprovalMode
  remainingApprovalMode?: BiddingApprovalMode
}

/** 1 つの target と、 その target が取る action を決める遷移後の承認モード・申請提出する札のエリア。 */
export type DraftSubmitEntry = {
  target: DraftSubmitTarget
  /**
   * 遷移後の承認モード (全札指定は保存済み、 一部指定は申請・提出する側)。 要承認なら承認申請、
   * 承認なし・自動なら提出する (backend も同じモードから action を導く)。
   */
  mode: BiddingApprovalMode
  /** 申請・提出する札のエリア (札ごと。 長さが札の件数)。 */
  areaCodes: readonly AreaCode[]
}

/**
 * 確認モーダルを開いた時点で確定した、 承認申請・提出の計画。 確認モーダルの表示 (件数・種類・
 * 送信の行・内訳) も、 確定後に送る targets も、 実行時の照合 ([[isDraftSubmitPlanCurrent]]) も
 * ここから導く。
 */
export type DraftSubmitPlan = {
  entries: readonly DraftSubmitEntry[]
}

const isSameTimestamp = (
  a: Timestamp | undefined,
  b: Timestamp | undefined,
): boolean => a?.seconds === b?.seconds && a?.nanos === b?.nanos

/**
 * 承認申請・提出する札 (`submitBidIds`) から、 申請セットごとの計画を組み立てる。
 *
 * - 申請セットの全札が対象 → 全札指定、 一部だけ → 一部指定 (承認モードは `resolveMode` で
 *   対象側と残りの側を別々に解決。 規則は `resolveCreateApprovalMode` の 1 か所)
 * - 対象の札が無い申請セット・下書きでない申請セットは target にしない
 * - 対象の札が下書きの申請セットに見つからなければ `undefined` (一覧と下書きが食い違っている)
 */
export const planDraftSubmit = (
  requests: readonly BiddingSpotApprovalRequest[],
  submitBidIds: ReadonlySet<string>,
  resolveMode: (areaCodes: readonly AreaCode[]) => BiddingApprovalMode,
): DraftSubmitPlan | undefined => {
  const foundBidIds = new Set<string>()
  const entries: DraftSubmitEntry[] = []
  for (const request of requests) {
    if (request.approvalState !== BiddingApprovalState.DRAFT) continue
    const submitBids = request.spotBids.filter((b) => submitBidIds.has(b.id))
    if (submitBids.length === 0) continue
    for (const bid of submitBids) foundBidIds.add(bid.id)
    const spotBidIds = submitBids.map((b) => b.id)
    const areaCodes = submitBids.map((b) => b.areaCode)
    if (submitBids.length === request.spotBids.length) {
      entries.push({
        target: {
          id: request.id,
          expectedUpdatedAt: request.updatedAt,
          spotBidIds,
        },
        mode: request.approvalMode,
        areaCodes,
      })
      continue
    }
    const approvalMode = resolveMode(areaCodes)
    entries.push({
      target: {
        id: request.id,
        expectedUpdatedAt: request.updatedAt,
        spotBidIds,
        approvalMode,
        remainingApprovalMode: resolveMode(
          request.spotBids
            .filter((b) => !submitBidIds.has(b.id))
            .map((b) => b.areaCode),
        ),
      },
      mode: approvalMode,
      areaCodes,
    })
  }
  if (foundBidIds.size !== submitBidIds.size) return undefined
  return { entries }
}

/** `TransitionBiddingSpotDrafts` に送る targets。 */
export const draftSubmitTargets = (
  plan: DraftSubmitPlan,
): DraftSubmitTarget[] => plan.entries.map((entry) => entry.target)

/**
 * 承認申請・提出の種類。 各 target が取る action から決める: 全員が承認申請なら `request`、
 * 全員が提出なら `direct`、 混ざる時は `mixed` (AC-BSP-015)。 対象が 0 件は `request`
 * (何もない状態で提出を名乗らない)。
 */
export type DraftSubmitKind = 'request' | 'direct' | 'mixed'

export const draftSubmitKind = (plan: DraftSubmitPlan): DraftSubmitKind => {
  const modes = plan.entries.map((entry) => entry.mode)
  if (isDirectSubmitMode(modes)) return 'direct'
  return modes.some((mode) => isDirectSubmitMode([mode])) ? 'mixed' : 'request'
}

/** 確認モーダルの「JEPX への送信」と取下の行 (AC-BSP-076) が示す送信のタイミング。 */
export const draftSubmitSendTiming = (
  kind: DraftSubmitKind,
): SubmitSendTiming => {
  switch (kind) {
    case 'direct':
      return 'onSubmit'
    case 'mixed':
      return 'mixed'
    case 'request':
      return 'afterApproval'
  }
}

/** 混在時の内訳の 1 行 (承認申請する側、 または提出する側)。 */
export type DraftSubmitSplitPart = {
  /** 札の件数。 */
  count: number
  /** エリア (JEPX の北 → 南の並び)。 */
  areaCodes: AreaCode[]
}

export type DraftSubmitSplit = {
  /** 要承認の target = 承認申請する。 */
  request: DraftSubmitSplitPart
  /** 承認なし・自動の target = そのまま提出する。 */
  submit: DraftSubmitSplitPart
}

const splitPartOf = (
  entries: readonly DraftSubmitEntry[],
): DraftSubmitSplitPart => {
  const areas = new Set(entries.flatMap((entry) => entry.areaCodes))
  return {
    count: entries.reduce(
      (sum, entry) => sum + entry.target.spotBidIds.length,
      0,
    ),
    areaCodes: JEPX_AREA_CODES.filter((code) => areas.has(code)),
  }
}

/**
 * 混在時の確認モーダルの内訳 (承認申請する札とそのまま提出する札の件数・エリア)。 件数とエリアは
 * 各 target の遷移後の承認モードで振り分け、 別に数え直さない。
 */
export const draftSubmitSplit = (plan: DraftSubmitPlan): DraftSubmitSplit => {
  const isSubmit = (entry: DraftSubmitEntry): boolean =>
    isDirectSubmitMode([entry.mode])
  return {
    request: splitPartOf(plan.entries.filter((entry) => !isSubmit(entry))),
    submit: splitPartOf(plan.entries.filter(isSubmit)),
  }
}

/**
 * 確認モーダルを開いた時点の申請セット / グループ (`expectedUpdatedAt`: ID → 更新日時) が、
 * 現在の一覧でもすべて下書きに残り、 更新日時も動いていないかを照合する (AC-BSP-124)。
 * 申請セットもブロックのグループも、 他のユーザーの更新は更新日時が進むことで見分ける。
 * すべて一致する時だけ `current` の並びで返し、 1 件でも欠けた (削除・提出された)・動いた時は
 * `undefined` (呼び出し側は書き込まずに中断する)。
 */
export const resolveUnchangedDrafts = <
  G extends {
    id: string
    approvalState: BiddingApprovalState
    updatedAt?: Timestamp
  },
>(
  current: readonly G[],
  expectedUpdatedAt: ReadonlyMap<string, Timestamp | undefined>,
): G[] | undefined => {
  const targets = current.filter(
    (g) =>
      g.approvalState === BiddingApprovalState.DRAFT &&
      expectedUpdatedAt.has(g.id),
  )
  if (targets.length !== expectedUpdatedAt.size) return undefined
  return targets.every((g) =>
    isSameTimestamp(g.updatedAt, expectedUpdatedAt.get(g.id)),
  )
    ? targets
    : undefined
}

/**
 * 確認モーダルを開いた時点の計画が、 最新の下書きの申請セットと照合して動いていないか。
 * 申請セットが下書きに残り更新日時が同じで、 計画の札がまだその申請セットにある時だけ true
 * (札の ID は申請セットの更新で振り直されるので、 見せた札と違う札を申請しない)。
 */
export const isDraftSubmitPlanCurrent = (
  plan: DraftSubmitPlan,
  requests: readonly BiddingSpotApprovalRequest[],
): boolean => {
  const unchanged = resolveUnchangedDrafts(
    requests,
    new Map(
      plan.entries.map(({ target }) => [target.id, target.expectedUpdatedAt]),
    ),
  )
  if (unchanged === undefined) return false
  return plan.entries.every(({ target }) => {
    const bidIds = new Set(
      unchanged.find((r) => r.id === target.id)?.spotBids.map((b) => b.id),
    )
    return target.spotBidIds.every((id) => bidIds.has(id))
  })
}
